import { Link } from "react-router-dom";
import { riskTags } from "../content";
import {
  CapabilityCard,
  HeroFlow,
  InfrastructureDiagram,
  PolicyDiagram,
  ReceiptCard,
  TrustLayerDiagram,
} from "../components/Diagrams";

export function HomePage() {
  return (
    <div className="home-page">
      <section className="hero-section section-grid">
        <div className="container hero-layout">
          <div className="hero-copy">
            <span className="eyebrow">TRUSTED AGENT INFRASTRUCTURE</span>
            <h1>
              Give AI agents authority.
              <br />
              <em>Without giving up control.</em>
            </h1>
            <p className="hero-lede">
              ORIQ provides delegated action control for autonomous agents —
              binding intent, authority, capabilities, policy, execution, and
              verification into a unified trust layer.
            </p>
            <div className="button-row">
              <a className="button" href="#trust-layer">
                Explore ORIQ <span aria-hidden="true">↓</span>
              </a>
              <Link className="button button-quiet" to="/whitepaper">
                Read Whitepaper <span aria-hidden="true">↗</span>
              </Link>
            </div>
            <div className="hero-proof">
              <span />
              <p>Trust before action. Proof after execution.</p>
            </div>
          </div>
          <HeroFlow />
        </div>
      </section>
      <ProblemSection />
      <section className="content-section trust-section" id="trust-layer">
        <div className="container">
          <SectionIntro
            eyebrow="THE ORIQ PROTOCOL"
            title="Trust, enforced at the point of action."
            body="ORIQ sits between autonomous reasoning and external consequence, establishing a verifiable trust chain between Principal Intent and external execution."
          />
          <TrustLayerDiagram />
        </div>
      </section>
      <DelegatedSection />
      <PolicySection />
      <VerificationSection />
      <InfrastructureSection />
      <NetworkSection />
      <section className="cta-section">
        <div className="container cta-inner">
          <span className="eyebrow">BUILD TRUSTED AUTONOMY</span>
          <h2>Autonomy requires trust.</h2>
          <p>
            Build agents that can act with bounded authority, verifiable
            execution, accountable outcomes, and recoverable failure.
          </p>
          <div className="button-row">
            <Link className="button" to="/whitepaper">
              Read the Whitepaper <span aria-hidden="true">↗</span>
            </Link>
            <Link className="button button-quiet" to="/protocol">
              Explore the Protocol <span aria-hidden="true">→</span>
            </Link>
          </div>
          <div className="cta-trace" aria-hidden="true">
            Intent <span>→</span> Authority <span>→</span> Capability{" "}
            <span>→</span> Action <span>→</span> Verification
          </div>
        </div>
      </section>
    </div>
  );
}

function ProblemSection() {
  return (
    <section className="content-section problem-section">
      <div className="container">
        <SectionIntro
          title="Agents can act. But who controls what they are allowed to do?"
          body="Autonomous agents can call APIs, operate cloud infrastructure, access enterprise systems, and move capital. Traditional credentials prove access, but they do not prove why an agent is acting, whether the action remains within delegated authority, or whether the resulting outcome is correct."
        />
        <div className="comparison-grid">
          <Comparison
            title="Traditional Agent Access"
            risk="Broad Execution Authority"
            steps={["AI Agent", "API Key / OAuth / Wallet", "External System"]}
          />
          <Comparison
            title="ORIQ-Controlled Execution"
            safe="Bounded Execution"
            steps={[
              "AI Agent",
              "Intent",
              "Authority",
              "Capability",
              "Policy",
              "External System",
            ]}
          />
        </div>
        <div className="tag-row">
          {riskTags.map((tag) => (
            <span className="risk-tag" key={tag}>
              {tag}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

function Comparison({
  title,
  steps,
  risk,
  safe,
}: {
  title: string;
  steps: string[];
  risk?: string;
  safe?: string;
}) {
  return (
    <div className={`comparison-card ${safe ? "is-safe" : "is-risk"}`}>
      <div className="card-label">
        {safe ? "ORIQ CONTROL" : "LEGACY ACCESS"}
      </div>
      <h3>{title}</h3>
      <div className="comparison-steps">
        {steps.map((step, index) => (
          <div key={step}>
            <span>{String(index + 1).padStart(2, "0")}</span>
            <strong>{step}</strong>
          </div>
        ))}
      </div>
      <div className="state-line">
        <i>{safe ? "✓" : "!"}</i>
        {safe || risk}
      </div>
    </div>
  );
}

function DelegatedSection() {
  return (
    <section className="content-section">
      <div className="container">
        <SectionIntro
          eyebrow="DELEGATED ACTION CONTROL"
          title="Give agents exactly the authority they need. Nothing more."
          body="ORIQ transforms broad credentials into bounded, purpose-specific execution capabilities that can be constrained, monitored, revoked, and verified."
        />
        <div className="split-layout">
          <div className="authority-trace">
            {[
              "Principal Authority",
              "Delegated Authority",
              "Scoped Capability",
              "Agent Action",
            ].map((item, index) => (
              <div className="authority-step" key={item}>
                <span>0{index + 1}</span>
                <strong>{item}</strong>
                {index < 3 && <i>↓</i>}
              </div>
            ))}
          </div>
          <CapabilityCard />
        </div>
        <div className="section-link">
          <Link className="arrow-link" to="/delegated-action-control">
            Explore Delegated Action Control <span aria-hidden="true">→</span>
          </Link>
        </div>
      </div>
    </section>
  );
}

function PolicySection() {
  return (
    <section className="content-section dark-panel" id="policy-engine">
      <div className="container">
        <SectionIntro
          eyebrow="POLICY ENGINE"
          title="Every consequential action passes through policy."
          body="ORIQ evaluates autonomous actions against identity, intent, delegated authority, capabilities, execution context, risk, and policy before external execution is permitted."
        />
        <PolicyDiagram />
        <div className="formula-line">
          Decision = F(Action, Intent, Authority, Capability, Context, Risk,
          Policy)
        </div>
      </div>
    </section>
  );
}

function VerificationSection() {
  return (
    <section className="content-section">
      <div className="container">
        <SectionIntro
          eyebrow="VERIFIABLE EXECUTION"
          title="Don't just log what agents do. Prove it."
          body="ORIQ creates cryptographic proofs and inspectable records connecting Principal Intent, delegated authority, policy decisions, execution events, and external outcomes."
        />
        <div className="split-layout receipt-layout">
          <div className="proof-list">
            {[
              "Intent Proof",
              "Authority Proof",
              "Capability Proof",
              "Policy Decision",
              "Execution Proof",
              "Outcome Attestation",
            ].map((item, index) => (
              <div key={item}>
                <span>0{index + 1}</span>
                <strong>{item}</strong>
                <i>✓</i>
              </div>
            ))}
          </div>
          <ReceiptCard />
        </div>
        <p className="center-note">
          A verifiable chain from Principal Intent to external outcome.
        </p>
        <div className="equation-pair">
          <span>Execution Success ≠ Outcome Success</span>
          <span>Settlement Proof ≠ Fulfillment Proof</span>
        </div>
        <div className="section-link">
          <Link className="arrow-link" to="/action-receipts">
            Explore Action Receipts <span aria-hidden="true">→</span>
          </Link>
        </div>
      </div>
    </section>
  );
}

function InfrastructureSection() {
  return (
    <section className="content-section light-panel">
      <div className="container">
        <SectionIntro
          eyebrow="INTEROPERABILITY"
          title="Built around the infrastructure you already use."
          body="ORIQ does not replace identity providers, APIs, MCP, key management, payment rails, cloud infrastructure, or blockchains. It connects them through a unified autonomous action-control layer."
        />
        <InfrastructureDiagram />
      </div>
    </section>
  );
}

function NetworkSection() {
  return (
    <section className="content-section">
      <div className="container">
        <SectionIntro
          eyebrow="THE ORIQ NETWORK"
          title="Trust infrastructure for the Agent Economy."
          body="ORIQ evolves from trusted action control into a shared verification environment where Agents, Principals, Providers, Verifiers, and Validators can coordinate across independent trust domains."
        />
        <div className="roadmap-strip">
          {[
            [
              "01",
              "TRUST PRIMITIVE",
              "Identity · Intent · Authority · Capability",
            ],
            [
              "02",
              "ACTION CONTROL",
              "Trust Gateway · Policy · Execution · Recovery",
            ],
            [
              "03",
              "VERIFICATION NETWORK",
              "Action Receipts · Verifiers · Validators",
            ],
            ["04", "AGENT ECONOMY", "Agents · Providers · Capital · Services"],
          ].map(([number, title, detail]) => (
            <div className="roadmap-phase" key={number}>
              <span>{number}</span>
              <strong>{title}</strong>
              <small>{detail}</small>
            </div>
          ))}
        </div>
        <div className="home-network">
          <div className="network-copy">
            <span className="eyebrow">LONG-TERM DIRECTION</span>
            <h3>From bounded action to shared trust.</h3>
            <p>
              The network begins with verifiable action control and expands
              toward independent verification across trust domains.
            </p>
            <Link className="arrow-link" to="/network">
              Explore the ORIQ Network <span aria-hidden="true">→</span>
            </Link>
          </div>
          <div className="network-mini">
            <div className="network-mini-line" />
            <span>Principal</span>
            <span>Agent</span>
            <span>Provider</span>
            <span>Verifier</span>
            <span>Validator</span>
          </div>
        </div>
      </div>
    </section>
  );
}

export function SectionIntro({
  eyebrow,
  title,
  body,
}: {
  eyebrow?: string;
  title: string;
  body?: string;
}) {
  return (
    <div className="section-intro">
      {eyebrow && <span className="eyebrow">{eyebrow}</span>}
      <h2>{title}</h2>
      {body && <p>{body}</p>}
    </div>
  );
}

export function PageHero({
  eyebrow,
  title,
  body,
  status,
  className,
}: {
  eyebrow: string;
  title: string;
  body: string;
  status?: string;
  className?: string;
}) {
  return (
    <section className={`page-hero ${className ?? ""}`}>
      <div className="container page-hero-inner">
        <div>
          <span className="eyebrow">{eyebrow}</span>
          <h1>{title}</h1>
          <p>{body}</p>
        </div>
        {status && (
          <span className="status-chip">
            <i />
            {status}
          </span>
        )}
      </div>
    </section>
  );
}

export function PageSection({
  eyebrow,
  title,
  body,
  children,
  id,
  className,
}: {
  eyebrow?: string;
  title: string;
  body?: string;
  children?: React.ReactNode;
  id?: string;
  className?: string;
}) {
  return (
    <section
      className={`content-section inner-section ${className ?? ""}`}
      id={id}
    >
      <div className="container">
        <SectionIntro eyebrow={eyebrow} title={title} body={body} />
        {children}
      </div>
    </section>
  );
}

export function StepList({ items }: { items: Array<[string, string]> }) {
  return (
    <div className="step-list">
      {items.map(([title, body], index) => (
        <div className="step-item" key={title}>
          <span>{String(index + 1).padStart(2, "0")}</span>
          <div>
            <h3>{title}</h3>
            <p>{body}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

export function DataCards({ items }: { items: Array<[string, string]> }) {
  return (
    <div className="data-cards">
      {items.map(([title, body]) => (
        <article className="data-card" key={title}>
          <span className="card-label">{title}</span>
          <p>{body}</p>
        </article>
      ))}
    </div>
  );
}

export function LinkCards({
  items,
}: {
  items: Array<[string, string, string]>;
}) {
  return (
    <div className="link-cards">
      {items.map(([title, body, href]) => (
        <Link className="link-card" to={href} key={title}>
          <span className="card-label">EXPLORE</span>
          <h3>{title}</h3>
          <p>{body}</p>
          <span className="arrow-link">
            Open <span aria-hidden="true">→</span>
          </span>
        </Link>
      ))}
    </div>
  );
}

export function ContactPage() {
  const submit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const subject = `ORIQ early access: ${String(data.get("company") || "New inquiry")}`;
    const body = [
      `Name: ${data.get("name") || ""}`,
      `Email: ${data.get("email") || ""}`,
      `Company / Project: ${data.get("company") || ""}`,
      "",
      String(data.get("message") || ""),
    ].join("\n");
    window.location.href = `mailto:info@oriqai.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };
  return (
    <>
      <PageHero
        eyebrow="BUILD WITH ORIQ"
        title="Build agents that can act with bounded authority."
        body="Interested in building with ORIQ? Get in touch with the team."
      />
      <PageSection
        title="Start a conversation"
        body="Use the form to prepare an email addressed to info@oriqai.com. Messages are forwarded to the ORIQ team inbox."
      >
        <form className="contact-form" onSubmit={submit}>
          <label>
            Name
            <input name="name" required autoComplete="name" />
          </label>
          <label>
            Email
            <input type="email" name="email" required autoComplete="email" />
          </label>
          <label>
            Company / Project
            <input name="company" required />
          </label>
          <label>
            Message
            <textarea name="message" required rows={6} />
          </label>
          <button className="button" type="submit">
            Submit inquiry <span aria-hidden="true">↗</span>
          </button>
        </form>
      </PageSection>
    </>
  );
}
